import React, { useMemo, useEffect, useState, useCallback, useRef } from 'react';

import i18n from 'i18n-js';
import moment from 'moment';
import { useQuery } from 'hooks';
import { buildUrl } from 'utils';
import Title from 'components/Title';
import Layout from 'components/Layout';
import Card from '@material-ui/core/Card';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Switch from '@material-ui/core/Switch';
import { useHistory } from 'react-router-dom';
import { Box, Container } from '@material-ui/core';
import { forceTimezone, toQueryString } from 'utils';
import Typography from '@material-ui/core/Typography';
import { Table, DateRangePicker } from 'components/UI';
import { useDispatch, useSelector } from 'react-redux';
import { useStyles, switchStyles } from './List.styled';
import { DocumentsActions } from 'state/modules/documents';
import { DocumentTypesSelect } from 'components/AsyncFields';
import VersionStatus from 'components/Documents/VersionStatus';
import NotificationsIcon from '@material-ui/icons/Notifications';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { debounce } from 'lodash';

function WidgetHeader({
  endDate,
  startDate,
  onlyExpired,
  handleParams,
  documentTypeId,
  handleCreationDateHandler,
}) {
  const classes = useStyles();
  const switchClasses = switchStyles();
  const [isExpired, setExpired] = useState(onlyExpired && onlyExpired !== 'false');
  const handleToggle = () => {
    setExpired(!isExpired);
    handleParams('is_expired', !isExpired);
  };
  return (
    <Box flex="1" display="flex" flexDirection="column">
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={2}>
          <DocumentTypesSelect
            form={{
              setFieldValue: handleParams,
            }}
            margin="none"
            field={{
              name: 'document_type_id',
              value: documentTypeId || '',
            }}
            firstLoad={documentTypeId ? true : false}
            label={i18n.t('documents.document_type')}
          />
        </Grid>
        <Grid item xs={12} sm="auto">
          <DateRangePicker
            showClearDates
            onDatesChange={handleCreationDateHandler}
            label={i18n.t('documents.creation_date')}
            endDate={endDate ? moment(endDate) : undefined}
            startDate={startDate ? moment(startDate) : undefined}
          />
        </Grid>
        <Grid item xs={10} sm={8} md={3} lg={4} className={classes.switchWrapper}>
          <FormControlLabel
            value="start"
            classes={switchClasses}
            control={
              <Switch
                color="primary"
                checked={isExpired}
                onChange={handleToggle}
                inputProps={{ 'aria-label': i18n.t('documents.show_only_expired') }}
              />
            }
            label={i18n.t('documents.show_only_expired')}
            labelPlacement="start"
          />
        </Grid>
      </Grid>
    </Box>
  );
}

function DocumentsPage() {
  const classes = useStyles();
  const urlParams = useQuery();
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    ...urlParams,
  });
  const history = useHistory();
  const dispatch = useDispatch();
  const documents = useSelector((state) =>
    state.documents.all.map((id) => state.documents.byId[id])
  );
  const pagination = useSelector((state) => state.documents.pagination);
  const isFetching = useSelector((state) => state.documents.loading);
  const { localeFormat } = useSelector((state) => state.organizations);
  const isMounted = useRef(false);
  const abortController = useRef(null);

  const columns = useMemo(
    () => [
      {
        name: i18n.t('tableColumns.id'),
        selector: 'id',
        sortable: true,
        grow: 0,
      },
      {
        name: i18n.t('tableColumns.document_type'),
        selector: 'document_type.name',
        grow: 1,
      },
      {
        name: i18n.t('tableColumns.related_to'),
        selector: 'source_id',
        cell: (row) => `${row.source_type} #${row.source_id}`,
        grow: 1,
      },
      {
        name: i18n.t('tableColumns.expiration_date'),
        selector: 'default.expiration_time',
        cell: (row) => {
          if (!row?.default?.expiration_time) {
            return '--';
          }
          const expiration = moment(row?.default?.expiration_time);
          return (
            <Box display="inline-flex" flexDirection="row" alignItems="center">
              {expiration.format(localeFormat)}
              {Boolean(expiration.isSameOrBefore()) && (
                <Box display="inline-flex" flexDirection="row" alignItems="center" ml={2}>
                  <NotificationsIcon color="error" fontSize="small" />
                  <Typography color="error" component="span" variant="caption">
                    {i18n.t('documents.expired')}
                  </Typography>
                </Box>
              )}
            </Box>
          );
        },
        grow: 1,
      },
      {
        name: i18n.t('tableColumns.creation_date'),
        selector: 'created_time',
        cell: (row) => (row?.created_time ? moment(row?.created_time).format(localeFormat) : '--'),
        sortable: true,
        grow: 1,
      },
      {
        name: i18n.t('common.status'),
        selector: 'status',
        cell: (row) =>
          row?.default?.status ? <VersionStatus status={row?.default?.status} /> : '--',
        grow: 1,
      },
    ],
    [] // eslint-disable-line
  );

  const handleRowClicked = (row) => {
    history.push(buildUrl(`/documents/${row?.id}`));
  };

  const fetchDocuments = useCallback(
    (params = {}) => {
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(
        DocumentsActions.fetchDocuments(params, { signal: abortController?.current?.signal })
      );
    },
    [dispatch]
  );

  const fetchDocumentsDebounced = useCallback(
    debounce((params) => fetchDocuments(params), 1000),
    [fetchDocuments]
  );

  useEffect(() => {
    if (isMounted.current) {
      fetchDocumentsDebounced(params);
    } else {
      isMounted.current = true;
    }
  }, [fetchDocumentsDebounced, params]);

  // Run once on mount, no debounce
  useEffect(() => {
    fetchDocuments(params);
  }, []);

  const handleParams = (params) => {
    const data = { ...params };
    setParams({ ...data });
    const _params = toQueryString(data);
    history.replace(buildUrl(`/documents${_params}`));
  };

  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle sort change on table
   * @param {string} column Column of the table
   * @param {string} sortDirection Sort direction
   */
  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
  };

  // change number of items per page
  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  const handleCreationDateHandler = (dates) => {
    const { startDate, endDate } = dates;
    let _params = { ...params, page: 1 };
    if (startDate) {
      _params.start_date = forceTimezone(startDate).startOf('day').format('YYYY-MM-DDTHH:mm:00Z');
    } else {
      delete _params.start_date;
    }
    if (!endDate && startDate) {
      _params.end_date = forceTimezone(startDate).endOf('day').format('YYYY-MM-DDTHH:mm:00Z');
    } else if (endDate && startDate) {
      _params.end_date = forceTimezone(endDate).endOf('day').format('YYYY-MM-DDTHH:mm:00Z');
    } else {
      delete _params.end_date;
    }

    handleParams(_params);
  };

  // search by status, keyword and organization
  const onSearch = (name, value) => {
    const filters = {
      ...params,
      page: 1,
      // validate if value is an object
      [name]: value,
    };
    // remove empty fields
    Object.keys(filters).forEach(
      (key) => !filters[key] && filters[key] !== false && delete filters[key]
    );
    handleParams({ ...filters });
  };

  return (
    <Layout title={i18n.t('sidebar.documents')}>
      <Container className={classes.container}>
        <Grid container spacing={2} style={{ paddingBottom: '25px' }}>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" alignItems="center" justifyContent="space-between">
              <Grid item sm={8}>
                <Title dataCy="documents-title" title={i18n.t('sidebar.documents')} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <WidgetHeader
            handleParams={onSearch}
            keyword={params.keyword}
            endDate={params.end_date}
            startDate={params.start_date}
            onlyExpired={params.is_expired}
            documentTypeId={params.document_type_id}
            handleCreationDateHandler={handleCreationDateHandler}
          />
        </Grid>
        <Grid item xs={12}>
          <Card style={{ marginTop: 32 }}>
            <Paper>
              <Table
                data={documents}
                columns={columns}
                page={params.page}
                onSort={handleSort}
                limit={params.limit}
                handlePageChange={handlePageChange}
                handleRowClicked={handleRowClicked}
                defaultSortAsc={!params?.sort_desc}
                defaultSortField={params?.sort_field}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
                loading={isFetching}
              />
            </Paper>
          </Card>
        </Grid>
      </Container>
    </Layout>
  );
}

export default DocumentsPage;
